import React, { useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, 
  Button, Typography, Box, IconButton, TextField, MenuItem, CircularProgress,
  List, ListItem, ListItemAvatar, ListItemText, Avatar, useMediaQuery, useTheme
} from '@mui/material';
import Grid from '@mui/material/Grid'; // Корректный импорт для MUI v5
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderIcon from '@mui/icons-material/Folder';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DocumentViewer from './DocumentViewer';

// Корректные пути импорта мутаций
import { 
  useAddProjectDocumentMutation, 
  useHideProjectDocumentMutation, 
  useUploadProjectImageMutation 
} from '../slices/projectsApiSlice';

const ProjectFilesManager = ({ open, onClose, project,onSuccess }) => {
  const theme = useTheme();
  // 1. Создаем стейт для управления открытым файлом
  const [selectedFile, setSelectedFile] = useState(null);
  // Обработчик закрытия вьювера
  const handleCloseViewer = () => {
    setSelectedFile(null);
  };

  // Модалка будет на весь экран на мобилках и планшетах
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [addProjectDocument] = useAddProjectDocumentMutation();
  const [toggleDocument, { isLoading }] = useHideProjectDocumentMutation();
  const [uploadMutation] = useUploadProjectImageMutation();

  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('');
  const [customName, setCustomName] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUploadClick = async () => {
    if (!file || !docType) return;
    
    const finalName = docType === 'Другое' ? customName.trim() : docType;
    if (!finalName) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file); 

      const uploadRes = await uploadMutation(formData).unwrap();
      
      if (uploadRes?.image) {
        await addProjectDocument({ 
          projectId: project._id, 
          name: finalName, 
          url: uploadRes.image 
        }).unwrap();
        
        setFile(null);
        setDocType('');
        setCustomName('');

		if (onSuccess) {
        	onSuccess();
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки документа:', error);
    } finally {
      setIsUploading(false);
    }
  };

const handleDelete = async (documentId) => {
    if (!window.confirm('Вы уверены, что хотите скрыть этот документ?')) return;
    
    try {
      await toggleDocument({ projectId: project?._id, documentId }).unwrap();
      if (onSuccess) onSuccess(); // Обновляем кэш RTK Query
    } catch (error) {
      console.error('Ошибка удаления:', error);
    }
  };

  const isUploadDisabled = !file || !docType || (docType === 'Другое' && !customName.trim()) || isUploading;

  return (<>
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullScreen={fullScreen} // Магия адаптивности!
      fullWidth 
      maxWidth="md"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'primary.contrastText', p: 2 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 500 }}>
          Файлы: {project?.title || 'Без названия'}
        </Typography>
        <IconButton onClick={onClose} color="inherit" size="large" edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={4} sx={{ mt: 0 }}>
          
          {/* ЛЕВАЯ КОЛОНКА: Компактный список файлов */}
          <Grid item xs={12} md={7} sx={{pr:2}}>
            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
              ЗАГРУЖЕННЫЕ ДОКУМЕНТЫ
            </Typography>
            
            {/* Строгая проверка массива через опциональную цепочку */}
            {!project?.specificationFiles || project?.specificationFiles?.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'grey.50', borderRadius: 2, mt: 1 }}>
                <AttachFileIcon color="disabled" sx={{ fontSize: 40 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Документы пока не добавлены
                </Typography>
              </Box>
            ) : (
              <List dense sx={{ width: '100%', bgcolor: 'background.paper', p: 0, mt: 1 }}>
                {project?.specificationFiles?.map((f, index) => {
					        const isHidden = f.visible === false;

                  return (<ListItem
                    key={f._id || index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      mb: 1.5,
					            bgcolor: isHidden ? 'action.hover' : 'background.paper',
					            opacity: isHidden ? 0.6 : 1,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                    }}
                    secondaryAction={
                      <IconButton 
                        edge="end" 
                        aria-label="delete" 
                        color={isHidden ? "primary" : "error"}
                        onClick={() => handleDelete(f?._id)} disabled={isLoading}
                        title={isHidden ? "Восстановить" : "Скрыть"}
                        >
                        {isLoading ? <CircularProgress size={16} /> : isHidden ? <RestoreFromTrashIcon  fontSize="small" /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: isHidden ? 'grey.400' : 'error.main' }}>
                        <AttachFileIcon />
                      </Avatar>
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          // Магия перечеркивания для удаленного файла:
                          textDecoration: isHidden ? 'line-through' : 'none',
                          color: isHidden ? 'text.disabled' : 'text.primary'
                        }}
                        >
                          {f.name}
                        </Typography>
                      }
                      secondary={
                        <Button 
                          size="small" 
                          // href={f.url} 
                          onClick={() => setSelectedFile(f)}
                          // target="_blank" 
                          // rel="noopener"
						              disabled={isHidden}
                          sx={{ p: 0, mt: 0.5, justifyContent: 'flex-start' }}
                        >
                          ОТКРЫТЬ ФАЙЛ
                        </Button>
                      }
                      sx={{ pr: 6 }} // Отступ для иконки удаления
                    />
                  </ListItem>)
				})}
              </List>
            )}
          </Grid>

          {/* ПРАВАЯ КОЛОНКА: Форма загрузки */}
          <Grid item xs={12} md={5} sx={{ borderLeft: { md: '1px solid #e0e0e0' }, pl: { md: 4 } }}>
            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
              ДОБАВИТЬ НОВЫЙ
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
              
              <Button 
                variant={file ? "contained" : "outlined"} 
                color={file ? "success" : "primary"}
                component="label" 
                startIcon={<CloudUploadIcon />} 
                fullWidth
                sx={{ py: 1.5, borderStyle: file ? 'solid' : 'dashed', borderWidth: 2 }}
              >
                {file ? file.name : 'Выбрать файл'}
                <input type="file" hidden accept=".pdf,.doc,.docx,image/jpeg,image/png" onChange={handleFileChange} />
              </Button>

              <TextField
                select
                fullWidth
                size="medium"
                label="Классификация"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
              >
                <MenuItem value="Договор">Договор</MenuItem>
                <MenuItem value="Проектная документация">Проектная документация</MenuItem>
                <MenuItem value="Смета">Смета</MenuItem>
                <MenuItem value="Акт выполненных работ">Акт выполненных работ</MenuItem>
                <MenuItem value="чертежи для сборки">чертежи для сборки</MenuItem>
                <MenuItem value="чертежи для монтажа">чертежи для монтажа</MenuItem>
                <MenuItem value="Другое">Другое...</MenuItem>
              </TextField>

              {docType === 'Другое' && (
                <TextField
                  fullWidth
                  size="medium"
                  label="Свое название"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  inputProps={{ maxLength: 50 }} // v5 syntax
                />
              )}

              <Button 
                onClick={handleUploadClick} 
                variant="contained" 
                size="large"
                disabled={isUploadDisabled}
                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ mt: 1, py: 1.5 }}
              >
                ЗАГРУЗИТЬ В БАЗУ
              </Button>
            </Box>
          </Grid>

        </Grid>
      </DialogContent>
    </Dialog>

      <DocumentViewer 
        open={Boolean(selectedFile)} 
        onClose={handleCloseViewer} 
        file={selectedFile} 
      />
      </>
  );
};

export default ProjectFilesManager;
